import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { Form, Formik } from 'formik';
import Box from '@mui/material/Box';
import clsx from 'clsx';
import Button from '@mui/material/Button';
import { useStyles } from '../../Categories/AddCategory';
import { editCustomerSchema } from '../../../../schemas/editCustomer.schema';
import CustomRadioButton from '../../../../common/CustomRadio';
import { userTypeOptions } from '../../../../constants/AppConst';
import UploadImage from '../../../../common/UploadImage';
import { onImageUpload } from '../../../../store/actions/uploadAction';
import { AppState } from '../../../../store';
import InputField from '../../../../common/InputField';
import CircularProgressBar from '../../../../common/CircularProgress';
import { SET_UPLOAD_URL, TopProductSellers } from '../../../../store/types/actions/ProductAction';
import { onUpdateSeller } from '../../../../store/actions/TopSeller';

interface EditSellerProps {
	setOpen: (open: boolean) => void;
	selectedSeller: TopProductSellers;
}

interface EditSellerState {
	name: string;
	email: string;
	isSeller: boolean;
	sellerName: string;
	sellerDescription: string
	rating: number;
	numReviews: number;
}

const EditSeller = (props: EditSellerProps) => {
	const { selectedSeller } = props;
	const defaultState = {
		name: '',
		email: '',
		isSeller: false,
		sellerName: '',
		sellerDescription: '',
		rating: 0,
		numReviews: 0
	}
	const [values, setSellerValues] = useState<EditSellerState>(defaultState)
	const classes: any = useStyles(props);
	const dispatch = useDispatch()
	const {imageUrl} = useSelector<AppState, AppState['products']>(
        ({products}) => products,
    );
	const {loading} = useSelector<AppState, AppState['common']>(
        ({common}) => common,
    );

	useEffect(() => {
		if(selectedSeller && selectedSeller.name) {
			setSellerValues({ 
				name: selectedSeller.name,
				email: selectedSeller.email,
				isSeller: selectedSeller.isSeller,
				sellerName: selectedSeller.seller.name,
				sellerDescription: selectedSeller.seller.description ,
				rating: selectedSeller.seller.rating,
				numReviews: selectedSeller.seller.numReviews
			})
			dispatch({ type: SET_UPLOAD_URL, payload: selectedSeller.seller.logo })
		}
	}, [dispatch, selectedSeller])

	const submitHandler = (data: EditSellerState, setSubmitting: any) => {
		const { sellerName, sellerDescription, rating, numReviews } = data
		const payload = {
			...data,
			seller: {
				name: sellerName,
				description: sellerDescription,
				logo: imageUrl,
				rating,
				numReviews
			}
		}
		setSubmitting(true);
		dispatch(onUpdateSeller(selectedSeller._id, payload))
		props.setOpen(false)
		setSubmitting(false);
	}

	const handleUserTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSellerValues({
			...values,
			isSeller: event.target.value === 'seller' ? true : false
		});
	}

	const onEditCancel = () => {
		props.setOpen(false)
		setSellerValues(defaultState)
	}

	const handleImageUpload = (event: any) => {
		dispatch({ type: SET_UPLOAD_URL, payload: null })
		const file = event.target.files[0];
        const bodyFormData = new FormData();
        bodyFormData.append('image', file);
		dispatch(onImageUpload(bodyFormData))
	}

	return (
		<Box display="flex" flexDirection="column">
			<Box component="h4" textAlign="center">Edit seller </Box>
			{ values && values.name &&
				<Paper className={classes.paper}>
					<Box flex={1} display='flex' p={2} flexDirection='column'>
						<Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
							<Grid item sm={1}></Grid>
							<Grid item sm={10}>
								<Formik
									validateOnChange={true}
									validationSchema={editCustomerSchema}
									initialValues={{ ...values }}
									onSubmit={(data, { setSubmitting }) => submitHandler(data, setSubmitting)}>
									{({ isSubmitting }) => (
										<Form noValidate autoComplete="off">
											<Box mb={{ xs: 5, xl: 8 }}>
												<InputField
													required
													id="customer-name"
													placeholder='name'
													name='name'
													label='Customer name'
													variant='standard'
												/>
											</Box>
											<Box mb={{ xs: 5, xl: 8 }}>
												<InputField
													required
													type="email"
													id="customer-email"
													placeholder='email'
													name='email'
													label='Email'
													variant='standard'
												/>
											</Box>
											<Box mb={{ xs: 5, xl: 8 }}>
												<CustomRadioButton
													legend="Select user type"
													options={userTypeOptions}
													name="isSeller"
													defaultValue={selectedSeller.isSeller ? 'seller' : 'customer'}
													handleChange={handleUserTypeChange}
												/>
											</Box>
											{values && values.isSeller && <>
												<Box mb={{ xs: 5, xl: 8 }}>
													<InputField
														required
														id="seller-name"
														placeholder='seller name'
														name='sellerName'
														label='Seller'
														variant='standard'
													/>
												</Box>
												<Box mb={{ xs: 5, xl: 8 }}>
													<InputField
														required
														id="seller-description"
														placeholder='description'
														name='sellerDescription'
														label='Description'
														variant='standard'
													/>
												</Box>
												<Box mb={{ xs: 2, xl: 3 }}>
													<UploadImage
														onChange={handleImageUpload}
														imageUrl={imageUrl || ''} 
													/>
													{loading && <CircularProgressBar /> }
												</Box>
												<Box mb={{ xs: 5, xl: 8 }}>
													<InputField
														required
														id="seller-rating"
														placeholder='rating'
														name='rating'
														label='Rating'
														variant='standard'
													/>
												</Box>
												<Box mb={{ xs: 5, xl: 8 }}>
													<InputField
														required
														id="seller-total-reviews"
														placeholder='Total reviews'
														name='numReviews'
														label='Total numbers of reviews'
														variant='standard'
													/>
												</Box></>
											}
											<Box
												mb={3}
												mr={3}
												display='flex'
												flexDirection={{ xs: 'column', sm: 'row' }}
												justifyContent='space-between'>
												<Button
													color="secondary"
													variant="contained"
													className={clsx(classes.signInButton)}
													onClick={() => onEditCancel()}
												>
													Cancel
												</Button>
												<Button
													color="primary"
													variant="contained"
													type="submit"
													disabled={isSubmitting}
													className={clsx(classes.signInButton)}>
													Update
												</Button>
											</Box>
										</Form>
									)}
								</Formik>
							</Grid>
							<Grid item sm={1}></Grid>
						</Grid>
					</Box>
				</Paper>
			}
		</Box>
	);
};

export default EditSeller;
